import React from 'react'
import {generatePath} from 'react-router'
import {Link as RouterLink} from 'react-router-dom'
import {Routes} from '../../../common/Routes'
import {Rental} from '../../../common/types/rental'
import {differenceInDays} from '../../../common/utils/dateUtils'
import {resolveBoxDisplayName} from '../../../common/utils/resolveBoxDisplayName'
import {roundToDigits} from '../../../common/utils/roundToDigits'
import {Link} from '../../../webui/Link'
import {SortablePaginatedTable} from '../../../webui/table/sortable-paginated-table/SortablePaginatedTable'
import {TableHeadCell} from '../../../webui/table/sortable-paginated-table/TableHeadCell'
import {Typography} from '../../../webui/Typography'
import {BoxForStats} from '../types/BoxForStats'
import {generateNumberOfRentalsPerBoxMap} from './utils/generateNumberOfRentalsPerBoxMap'

interface Props {
  rentals: Rental[]
  boxes: BoxForStats[]
  endDate: string | undefined
  fromDate: string | undefined
}

interface BoxData {
  id: string
  name: string
}

interface RentalsPerBoxTableData {
  id: string
  box: BoxData
  numberOfCompartments: number | string
  numberOfRentals: number
  numberOfRentalsPerCompartment: number | string
  averageRentalsPerDay: number | string
  averageRentalsPerDayPerCompartment: number | string
  share: number
}

const createTableHeadCells = (): TableHeadCell<RentalsPerBoxTableData>[] => [
  {
    id: 'box',
    label: 'Box',
    render: (row) => (
      <Link
        component={RouterLink}
        to={generatePath(Routes.BOX, {
          id: row['box'].id,
        })}
      >
        {row['box'].name}
      </Link>
    ),
  },
  {
    id: 'numberOfCompartments',
    label: `#Compartments`,
    render: (row) => row.numberOfCompartments,
  },
  {
    id: 'numberOfRentals',
    label: `#Rentals`,
    render: (row) => row.numberOfRentals,
  },
  {
    id: 'numberOfRentalsPerCompartment',
    label: `#Rentals per compartment`,
    render: (row) =>
      typeof row.numberOfRentalsPerCompartment === 'number' ? roundToDigits(row.numberOfRentalsPerCompartment, 1) : row.numberOfRentalsPerCompartment,
  },
  {
    id: 'averageRentalsPerDay',
    label: 'Average per day',
    render: (row) => row.averageRentalsPerDay,
  },
  {
    id: 'averageRentalsPerDayPerCompartment',
    label: 'Average per day per compartment',
    render: (row) => row.averageRentalsPerDayPerCompartment,
  },
  {
    id: 'share',
    label: 'Share',
    render: (row) => `${row.share}%`,
  },
]

export const RentalsPerBoxTable: React.FC<Props> = ({rentals, boxes, endDate, fromDate}) => {
  const end = endDate ? new Date(endDate) : new Date()
  const from = fromDate ? new Date(fromDate) : undefined

  // Build a reliable display name map from rentals, because Rental.box includes Location.
  const displayById: Record<string, string> = {}
  rentals.forEach((r) => {
    const id = r.box?.id
    if (!id) return
    if (!displayById[id]) {
      displayById[id] = resolveBoxDisplayName(r.box as any)
    }
  })

  const rentalsPerBox = generateNumberOfRentalsPerBoxMap(rentals)

  const rows: RentalsPerBoxTableData[] = Object.entries(rentalsPerBox).map(([boxId, numberOfRentals]) => {
    const box = boxes.find((b) => b.id === boxId)

    const start = from ?? (box?.initialActivationTime ? new Date(box.initialActivationTime) : null)

    const numberOfDaysSinceActivation = start ? differenceInDays(end, start) + 1 : null

    const share = rentals.length > 0 ? Math.round((numberOfRentals / rentals.length) * 100) : 0

    if (box && box.compartments?.length) {
      const compartmentsCount = box.compartments.length

      const averageRentalsPerDay = numberOfDaysSinceActivation
        ? roundToDigits(numberOfRentals / numberOfDaysSinceActivation, 2)
        : 'No activation date for box'

      const averageRentalsPerDayPerCompartment = numberOfDaysSinceActivation
        ? roundToDigits(numberOfRentals / numberOfDaysSinceActivation / compartmentsCount, 3)
        : 'No activation date for box'

      return {
        id: boxId,
        box: {
          id: boxId,
          name: displayById[boxId] ?? resolveBoxDisplayName(box as any),
        },
        numberOfCompartments: compartmentsCount,
        numberOfRentals,
        numberOfRentalsPerCompartment: numberOfRentals / compartmentsCount,
        averageRentalsPerDay,
        averageRentalsPerDayPerCompartment,
        share,
      }
    }

    return {
      id: boxId,
      box: {
        id: boxId,
        name: displayById[boxId] ?? boxId,
      },
      numberOfCompartments: 'Unknown',
      numberOfRentals,
      numberOfRentalsPerCompartment: 'Unknown',
      averageRentalsPerDay: numberOfDaysSinceActivation
        ? roundToDigits(numberOfRentals / numberOfDaysSinceActivation, 2)
        : 'No activation date for box',
      averageRentalsPerDayPerCompartment: 'Unknown',
      share,
    }
  })

  if (rows.length <= 0) {
    return <Typography>{`There were no rentals during this period`}</Typography>
  }

  return (
    <SortablePaginatedTable
      title={`Number of rentals per box`}
      initialRowsPerPage={rows.length}
      initialOrderByKey={'averageRentalsPerDay'}
      initialOrder={'desc'}
      tableHeadCells={createTableHeadCells()}
      rows={rows}
    />
  )
}
